export default class InlineDatePicker {
  constructor(container) {
    // 1. Cibler le conteneur principal de CE calendrier spécifique
    // (élément DOM ou sélecteur CSS)
    this.container = typeof container === 'string'
      ? document.querySelector(container)
      : container;

    if (!this.container) {
      console.error(`Le conteneur ${container} est introuvable.`);
      return;
    }

    // 2. Récupérer les éléments du DOM à l'intérieur de ce conteneur
    this.dateInput = this.container.querySelector('.date-input');
    this.monthYearDisplay = this.container.querySelector('.month-year');
    this.calendarGrid = this.container.querySelector('.calendar-grid');
    this.prevMonthBtn = this.container.querySelector('.prev-month');
    this.nextMonthBtn = this.container.querySelector('.next-month');
    this.calendar = this.container.querySelector('.calendar');

    // 3. Initialiser l'état (les données)
    this.currentDate = new Date(); // Mois affiché en ce moment
    this.selectedDate = null;      // Date cliquée par l'utilisateur
    this.referenceDate = this.parseCurrentDate();
    this.minDate = this.parseDataDate('dateMin');
    this.maxDate = this.parseDataDate('dateMax');

    if (this.referenceDate) {
      this.currentDate = new Date(this.referenceDate.getFullYear(), this.referenceDate.getMonth(), 1);
    }

    this.monthNames = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];

    // 4. Lancer la machine
    this.init();
  }

  normalizeDate(date) {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  parseDataDate(attributeName) {
    if (!this.calendar) {
      return null;
    }

    const dateString = this.calendar.dataset[attributeName];
    if (!dateString) {
      return null;
    }

    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      return null;
    }

    const parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);

    if (
      parsedDate.getFullYear() !== parts[0] ||
      parsedDate.getMonth() !== parts[1] - 1 ||
      parsedDate.getDate() !== parts[2]
    ) {
      return null;
    }

    return this.normalizeDate(parsedDate);
  }

  isDateInRange(date) {
    const normalizedDate = this.normalizeDate(date);

    if (this.minDate && normalizedDate < this.minDate) {
      return false;
    }

    if (this.maxDate && normalizedDate > this.maxDate) {
      return false;
    }

    return true;
  }

  // Initialisation des événements et du premier affichage
  init() {
    this.bindEvents();
    this.renderCalendar();
  }

  // Gestion des clics sur "Précédent" et "Suivant"
  bindEvents() {
    // L'utilisation des fonctions fléchées () => permet de conserver le bon "this"
    this.prevMonthBtn.addEventListener('click', () => {
      this.currentDate.setMonth(this.currentDate.getMonth() - 1);
      this.renderCalendar();
    });

    this.nextMonthBtn.addEventListener('click', () => {
      this.currentDate.setMonth(this.currentDate.getMonth() + 1);
      this.renderCalendar();
    });
  }

  // Lecture et validation de la date initiale venant de data-current-date (format YYYY-MM-DD)
  parseCurrentDate() {
    if (!this.calendar) {
      return null;
    }

    const dateString = this.calendar.dataset.currentDate;
    if (!dateString) {
      return null;
    }

    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      return null;
    }

    const parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);

    if (
      parsedDate.getFullYear() !== parts[0] ||
      parsedDate.getMonth() !== parts[1] - 1 ||
      parsedDate.getDate() !== parts[2]
    ) {
      return null;
    }

    return parsedDate;
  }

  // Construction de la grille des jours
  renderCalendar() {
    this.calendarGrid.innerHTML = '';
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    // Mise à jour de l'en-tête (ex: "Mars 2026")
    this.monthYearDisplay.textContent = `${this.monthNames[month]} ${year}`;

    // Calcul du premier jour du mois (Ajusté pour que Lundi = 0, Dimanche = 6)
    let firstDay = new Date(year, month, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1; 

    // Nombre de jours dans ce mois
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Remplir les cases vides du début du mois
    for (let i = 0; i < firstDay; i++) {
      const emptyCell = document.createElement('button');
      emptyCell.disabled = true;
      this.calendarGrid.appendChild(emptyCell);
    }

    // Créer les boutons pour chaque jour
    for (let i = 1; i <= daysInMonth; i++) {
      const dayBtn = document.createElement('button');
      dayBtn.textContent = i;
      const dayDate = new Date(year, month, i);
      const isDisabledByRange = !this.isDateInRange(dayDate);

      if (isDisabledByRange) {
        dayBtn.disabled = true;
      }
      
      // Ajouter la classe .selected si c'est la date choisie
      if (this.selectedDate && 
          this.selectedDate.getDate() === i && 
          this.selectedDate.getMonth() === month && 
          this.selectedDate.getFullYear() === year) {
        dayBtn.classList.add('selected');
      }

      if (
        this.referenceDate &&
        this.referenceDate.getDate() === i &&
        this.referenceDate.getMonth() === month &&
        this.referenceDate.getFullYear() === year
      ) {
        dayBtn.classList.add('current-billing-date');
        if (this.selectedDate !== null) {
          dayBtn.classList.add('old_current-billing-date');
        }
      }

      // Gérer le clic sur un jour
      if (!isDisabledByRange) {
        dayBtn.addEventListener('click', () => this.handleDayClick(year, month, i));
      }

      this.calendarGrid.appendChild(dayBtn);
    }
  }

  // Action exécutée au clic sur un jour
  handleDayClick(year, month, day) {
    const nextSelectedDate = new Date(year, month, day);
    if (!this.isDateInRange(nextSelectedDate)) {
      return;
    }
    this.container.querySelector('.btn-date-edit').classList.remove('hide');
    this.selectedDate = nextSelectedDate;
    this.dateInput.value = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    this.renderCalendar(); // On re-rend pour déplacer la couleur bleue
  }
}